import { OnDestroy, OnInit, Directive } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { map, skipWhile, take, takeUntil } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { sortByLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { ILocation } from '../shared/services/api/i-location.models';
import { IGeoLocationService } from './services/i-geo-location.service';

@Directive()
export abstract class BaseLocationSelector implements OnInit, OnDestroy {
  public availableLocations$: Observable<ILocation[]>;
  public control$: Observable<UntypedFormControl>;

  public TranslationKeys = TranslationKeys;

  private destroySubject$ = new Subject<void>();

  public constructor(
    protected readonly store: Store,
    protected readonly getLocationService: IGeoLocationService,
    protected readonly permissionManagerService: IPermissionManagerService,
  ) {}

  public ngOnInit(): void {
    this.getLocationService.loadLocationInformation();
    this.availableLocations$ = this.permissionManagerService.getGlobalPermissionsModel$().pipe(
      skipWhile((x) => !x),
      map((x) => sortByLocale(x.locations, (item) => item.name)),
    );

    this.control$ = this.availableLocations$.pipe(
      skipWhile((x) => !x),
      take(1),
      map((locations) => {
        const control = new UntypedFormControl(locations.find((l) => l.isCurrent));

        control.valueChanges.pipe(takeUntil(this.destroySubject$)).subscribe((val) => this.onLocationSelected(val));

        return control;
      }),
    );
  }

  public abstract onLocationSelected(location: ILocation);

  public ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
